/**
 * FeedbackModal — rating + free-text feedback dialog.
 *
 * Composition over the `Dialog` primitive (Radix-backed). Radix owns the
 * headless behaviour — portal mounting, focus trap, Escape-key dismissal,
 * overlay-click dismissal, and the ARIA role/title/description wiring
 * surfaced via `Dialog.Title`. This component owns the *contract* layered
 * on top of Radix: a 1-5 rating row, an optional free-text textarea, a
 * submit affordance, and a post-submit success acknowledgement. Reusing
 * the primitive (rather than rolling our own overlay) is what lets this
 * file stay focused on the rating-modal behaviour without re-litigating
 * accessibility plumbing the primitive already handles.
 *
 * Backend posture (033 US5, T040): feedback is PERSISTED — the FR-057
 * session-local stub (a bare `setSubmitted(true)`, no network call) is
 * retired. Submit awaits `member.feedback.submit` through
 * `FeedbackSubmitPort` and only then acknowledges; the API keys the row
 * by the session-derived pseudonym, so the client sends no identifier.
 * One `phase` discriminant (the DeleteAccountConfirm posture): `form` →
 * `pending` (submit disabled, double-submit guarded) → `done`
 * (acknowledgement + auto-close) or `error` (retryable, input preserved).
 *
 * The port is TOLERATED as absent (`useSubmitFeedback` yields `null`):
 * this modal is shell chrome on eligible member routes (see
 * `showsFloatingFeedback`), so a composition
 * without it — a focused test render, the CMS preview frame — must not
 * crash unrelated surfaces. Absent, the submit affordance is not rendered
 * at all, rather than offering a button that cannot persist or
 * acknowledging a success that never happened (P7: honest degrade).
 *
 * Proto-anchored contract: this replicates the *user-visible behaviour*
 * of the customer prototype's feedback modal — title "Anna palautetta",
 * a five-circle rating row with edge labels under #1 and #5, a
 * free-text textarea, a single submit button, and a "thank you" message
 * before auto-close. We do not import or copy the proto's code; the
 * modal contract is what we replicate, not the implementation.
 *
 * Accessibility posture: Radix handles focus trap + Escape + overlay
 * click via the primitive. The rating row is a `<fieldset>` with a
 * screen-reader-only `<legend>` so AT users hear "Anna arvosana 1–5"
 * before the five buttons. Each circle is a `<button type="button">`
 * with `aria-pressed` reflecting the single-selection state and an
 * `aria-label` localized via `home.feedback.ratingLabel` ("Arvosana
 * {n}" / "Rating {n}"). Submit stays disabled until a rating or
 * non-whitespace free text is supplied, structurally rather than through a
 * runtime validation message.
 *
 * `Dialog.Description` is mounted sr-only alongside `Dialog.Title`:
 * Radix wires the description into `aria-describedby` automatically,
 * which both silences the runtime warning ("Dialog requires a
 * Description") and gives AT users a short orienting sentence after
 * the title. The visual surface is unaffected because the description
 * carries `sr-only`.
 *
 * Success acknowledgement uses a pre-mounted `role="status"` polite
 * live region rather than a conditionally-mounted one. The region is
 * always present in the DOM (sr-only and empty pre-submit, visible
 * post-submit) because mount-and-fill in the same render is
 * unreliable across AT stacks — VoiceOver on Safari in particular
 * can miss the announcement when the element and its text appear
 * together. Pre-mounting lets AT register the region first, then
 * announce the content change on submit.
 */

import { type JSX, useEffect, useId, useRef, useState } from 'react';
import { useSubmitFeedback } from '../feedback/useSubmitFeedback.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Dialog } from '../primitives/Dialog.js';
import { CloseIcon } from './icons.js';

export interface FeedbackModalProps {
  readonly open: boolean;
  readonly onCloseAutoFocus?: ((event: Event) => void) | undefined;
  readonly onOpenChange: (next: boolean) => void;
}

const RATINGS: readonly [1, 2, 3, 4, 5] = [1, 2, 3, 4, 5] as const;

const SUCCESS_AUTO_CLOSE_MS = 1500;

/**
 * The submit lifecycle. One discriminant, no orthogonal booleans:
 *   form    — the rating row + textarea are editable
 *   pending — the write is in flight; submit disables
 *   done    — persisted; the acknowledgement shows and auto-close arms
 *   error   — the write failed; the member's input is preserved for a retry
 */
type Phase = 'form' | 'pending' | 'done' | 'error';

const ratingRowClasses = 'flex justify-center gap-3';
// `w-11` pins each column to the circle width (44px) so the rating row's
// circles stay evenly spaced. Without this, columns 1 and 5 — the only
// ones carrying edge labels — would expand to the label's natural width,
// throwing the row's geometry off. The edge label overflows the column
// width visibly; see `edgeLabelClasses`.
const ratingColumnClasses = 'flex flex-col items-center gap-1 w-11';
// `transition-all` (vs `transition-colors`) so the `hover:scale-110`
// transform animates alongside the colour swap. Tailwind v4 does NOT
// auto-guard transform utilities behind `prefers-reduced-motion` — it
// only zeroes the transition `duration`, so without an explicit guard
// the element would still snap to 110% size on hover for users who
// asked for reduced motion. `motion-reduce:hover:scale-100` cancels the
// scale entirely under `prefers-reduced-motion: reduce`, leaving only
// the colour swap (which is motion-neutral).
const ratingCircleBaseClasses =
  'inline-flex items-center justify-center rounded-full h-11 w-11 text-sm font-semibold ' +
  'transition-all hover:scale-110 motion-reduce:hover:scale-100 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const ratingCircleSelectedClasses = 'bg-primary text-primary-foreground';
// Unselected hover fills the circle with the brand-primary token and
// inverts the text — same posture as the proto, so hovering a 1-5 circle
// previews what selecting it will look like. The border colour shifts to
// match the new fill so the edge stays crisp during the transition.
const ratingCircleUnselectedClasses =
  'bg-popover text-foreground border border-border ' +
  'hover:bg-primary hover:text-primary-foreground hover:border-primary';
// `w-16` is wider than the `w-11` column so the Finnish labels
// ("Erittäin tyytymätön" / "Erittäin tyytyväinen") have room to wrap to
// 2-3 short lines without forcing the column to expand. `leading-tight`
// keeps the multi-line label compact. The label overflows the column on
// each side; the surrounding row gap (`gap-3` = 12px) provides clearance.
const edgeLabelClasses = 'text-xs text-muted-foreground text-center leading-tight w-16';
const textareaClasses =
  'w-full min-h-24 resize-none rounded-md border border-border bg-popover px-3 py-2 text-sm ' +
  'text-foreground placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const submitClasses =
  'inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 ' +
  'text-sm font-semibold text-primary-foreground transition-colors ' +
  'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
// Close X — proto-parity styling. The previous `h-8 w-8` square with a
// `rounded-md` token-popover hover landed the X at roughly the same
// vertical row as the centered `Dialog.Title`, so the two visually
// overlapped on the top edge of the inner column. Proto's posture is a
// smaller rounded-full pill (~28×28: `h-5 w-5` icon + `p-1` padding) that
// fills with the brand-primary token on hover and scales up slightly, the
// same micro-interaction the rating circles in this file already use.
// Pairing this tighter chrome with `pt-2` on `contentInnerClasses` gives
// the title enough room to clear the X without bumping Dialog.Content's
// shared `p-4` (which other dialogs — notably the 016 onboarding wizard
// — depend on). `motion-reduce:hover:scale-100` keeps the hover swap
// motion-neutral under `prefers-reduced-motion: reduce`.
//
// Tap-target posture: the visible chrome is ~28×28 (`h-5 w-5` icon +
// `p-1`), which clears WCAG 2.2 SC 2.5.8 *Target Size (Minimum)* (AA,
// 24×24) but is below WCAG 2.1 SC 2.5.5 *Target Size* (AAA, 44×44).
// D-PD-006 documents the AAA target for the TopBar hamburger
// specifically (a frequently-used global affordance); it is not a
// project-wide minimum, and other surfaces are free to land between
// WCAG 2.2 AA and WCAG 2.1 AAA based on UX trade-offs and the number
// of compensating affordances. Here the modal carries two additional
// dismissal paths Radix wires automatically — Escape and overlay
// click — so the X is one of three dismissals, which is the
// compensating context that lets the visible chrome stay at proto-
// fidelity. If a future review pushes for AAA on this control too,
// the standard pattern is to grow the hit area invisibly (e.g., a
// `before:absolute before:inset-[…]` pseudo-element) without changing
// the visible chrome.
const closeButtonClasses =
  'absolute top-3 right-3 inline-flex items-center justify-center rounded-full p-1 ' +
  'text-muted-foreground transition-all hover:bg-primary hover:text-primary-foreground ' +
  'hover:scale-110 motion-reduce:hover:scale-100 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const successClasses = 'text-sm text-foreground text-center py-4';
const errorClasses = 'text-sm text-foreground text-center';
const titleClasses = 'text-base font-semibold text-foreground text-center';
// `sr-only` keeps the description hidden visually while remaining
// announceable to AT — Radix wires it into `aria-describedby` so the
// dialog satisfies the ARIA "describedby" contract without disturbing
// the proto-pinned visual surface (no visible subtitle in the prototype).
const descriptionClasses = 'sr-only';
const formColumnClasses = 'flex flex-col gap-5';
// `sr-only` is the canonical screen-reader-only utility; it is part of
// Tailwind's default utility set and not an arbitrary-bracket class.
const legendClasses = 'sr-only';
const fieldsetClasses = 'border-0 p-0 m-0';
// `space-y-5` adds vertical rhythm between Title and the next in-flow
// child (the form `<div>` or the success `<p>`). The sr-only
// Description and pre-mounted live region are taken out of flow by their
// `sr-only` positioning, so they do not add visual spacing rows.
// `pt-2` (8px) reserves vertical room above the centered Title so the
// X close button (positioned absolute top-3 right-3) does not overlap
// the title text on the same baseline — see the `closeButtonClasses`
// comment for why we don't bump `Dialog.Content`'s shared `p-4`.
const contentInnerClasses = 'relative space-y-5 pt-2';

/** Controlled feedback dialog; callers key new open sessions to reset the form. */
export function FeedbackModal({
  open,
  onOpenChange,
  onCloseAutoFocus,
}: FeedbackModalProps): JSX.Element {
  const t = useTranslate();
  const submitFeedback = useSubmitFeedback();
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [phase, setPhase] = useState<Phase>('form');
  const inFlight = useRef(false);
  const submitted = phase === 'done';
  const trimmedFeedbackText = feedbackText.trim();
  const hasSubmissionContent = selectedRating !== null || trimmedFeedbackText.length > 0;
  // Stable id wires the sr-only `<label>` to the `<textarea>` via
  // `htmlFor`. Placeholder text is not a reliable accessible name for
  // assistive tech; an explicit label is the contract AT needs to
  // announce the field as "Free-form feedback (textbox)" rather than
  // just "textbox".
  const textareaId = useId();

  // Reset on (re)open is no longer an Effect. `FloatingFeedbackTab`
  // keys this component on an open-session counter (bumped only on
  // closed→open), so every fresh open mounts a new instance and
  // `useState`'s initialisers (null rating, empty text, not-submitted)
  // ARE the reset. The close does NOT bump the counter, so the instance
  // stays mounted long enough for Radix react-presence to animate the
  // exit (R10 — keys to reset state; R11 — Effects only for external
  // sync). State-from-prop mirroring via an Effect was the anti-pattern
  // the structural key removes.

  // Auto-close after submit acknowledgement. The timer is owned by the
  // success state — if the dialog is closed manually before the timer
  // fires, the unmount/cleanup clears the timeout.
  //
  // `onOpenChange` is in the dependency list per the exhaustive-deps
  // rule. The only caller (`FloatingFeedbackTab`) passes
  // `handleOpenChange` — a component-body arrow, NOT a `useState`
  // setter. In production the React Compiler is expected to memoise
  // that arrow (everything it captures — `setOpen`, `setOpenSession` —
  // is a stable state setter), so its identity is stable across the
  // parent's renders and this effect does not re-run on unrelated
  // parent re-renders. Without the compiler (e.g. the test env, which
  // runs unminified/uncompiled) `handleOpenChange` is recreated each
  // parent render; this effect's dependency on its identity means a
  // parent re-render between submit and the 1500ms deadline would
  // cancel-and-restart the timer, deferring the auto-close. That is the
  // accurate current behavior — there is no stable-identity guarantee
  // absent the compiler; the design tolerates it because the parent
  // does not re-render in that window in practice (its only state is
  // the open boolean + session counter, neither of which changes
  // between submit and auto-close).
  useEffect(() => {
    if (!submitted) {
      return undefined;
    }
    const handle = setTimeout(() => {
      onOpenChange(false);
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => {
      clearTimeout(handle);
    };
  }, [submitted, onOpenChange]);

  // 033 US5 (T040): the real persisted submit. `setSubmitted` is gated on the
  // RESOLVED promise, so the acknowledgement (and the auto-close it arms) only
  // appears once the row has committed server-side — a failure surfaces the
  // retryable error state with the member's input intact, never a fake success
  // (P7). The null-port arm is defence in depth (no affordance is rendered).
  //
  // `inFlight` is a ref, not `phase`: the guard must hold at CALL time, since
  // `disabled` only lands once React commits the pending render. Each submit
  // writes an unaudited `member_feedback` row, so a duplicate is not free.
  async function submit(): Promise<void> {
    if (submitFeedback === null || !hasSubmissionContent || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setPhase('pending');
    try {
      await submitFeedback({
        ...(selectedRating === null
          ? {}
          : {
              // `selectedRating` is narrowed to 1-5 by the RATINGS row that
              // sets it; the cast carries that invariant across the `number`
              // state type.
              rating: selectedRating as 1 | 2 | 3 | 4 | 5,
            }),
        ...(trimmedFeedbackText.length === 0 ? {} : { text: trimmedFeedbackText }),
      });
      setPhase('done');
    } catch {
      // The error is surfaced in the UI, not swallowed: the member sees the
      // retry prompt and their rating + text are still in state.
      setPhase('error');
    } finally {
      inFlight.current = false;
    }
  }

  const ratingLabelTemplate = t('home.feedback.ratingLabel');

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content onCloseAutoFocus={onCloseAutoFocus}>
        <div className={contentInnerClasses}>
          <Dialog.Close aria-label={t('home.feedback.close')} className={closeButtonClasses}>
            <CloseIcon className="h-5 w-5" />
          </Dialog.Close>
          <Dialog.Title className={titleClasses}>{t('home.feedback.title')}</Dialog.Title>
          <Dialog.Description className={descriptionClasses}>
            {t('home.feedback.description')}
          </Dialog.Description>
          {/*
           * Live region is pre-mounted so AT registers it before its
           * content changes — mount-and-fill in the same render is
           * unreliable across AT stacks (notably VoiceOver/Safari can
           * miss the announcement when the live-region element and its
           * text content appear in the same render). When `submitted`
           * is false the region is `sr-only` and empty, so it has zero
           * visual impact while the form is visible; on submit, the
           * form unmounts and `successClasses` makes the region the
           * visible content where the form used to sit. After
           * submission Radix moves focus to `Dialog.Close` — without
           * this polite live region, screen-reader users would never
           * hear the success acknowledgement.
           */}
          <p role="status" className={submitted ? successClasses : 'sr-only'}>
            {submitted ? t('home.feedback.success') : ''}
          </p>
          {!submitted && (
            <div className={formColumnClasses}>
              <fieldset className={fieldsetClasses}>
                <legend className={legendClasses}>{t('home.feedback.legend')}</legend>
                <div className={ratingRowClasses}>
                  {RATINGS.map((rating) => {
                    const selected = selectedRating === rating;
                    const circleClasses = `${ratingCircleBaseClasses} ${
                      selected ? ratingCircleSelectedClasses : ratingCircleUnselectedClasses
                    }`;
                    // `{n}` placeholder interpolation: `useTranslate`
                    // returns the raw string; we substitute the single
                    // `{n}` token here. Keeping the substitution local
                    // avoids inventing a new i18n contract for one
                    // template-bearing key.
                    const ariaLabel = ratingLabelTemplate.replace('{n}', String(rating));
                    return (
                      <div key={rating} className={ratingColumnClasses}>
                        <button
                          type="button"
                          aria-pressed={selected}
                          aria-label={ariaLabel}
                          onClick={() => setSelectedRating(rating)}
                          className={circleClasses}
                        >
                          {rating}
                        </button>
                        {rating === 1 ? (
                          <span className={edgeLabelClasses}>{t('home.feedback.edgeMin')}</span>
                        ) : null}
                        {rating === 5 ? (
                          <span className={edgeLabelClasses}>{t('home.feedback.edgeMax')}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </fieldset>
              <label htmlFor={textareaId} className="sr-only">
                {t('home.feedback.textareaLabel')}
              </label>
              <textarea
                id={textareaId}
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                placeholder={t('home.feedback.textareaPlaceholder')}
                className={textareaClasses}
              />
              {phase === 'error' && (
                <p role="alert" className={errorClasses}>
                  {t('home.feedback.error')}
                </p>
              )}
              {/* No port wired → no submit affordance. Rendering a button that
                  cannot persist, or acknowledging a success that never
                  happened, would be the P7 fake-continuity smell. */}
              {submitFeedback !== null && (
                <button
                  type="button"
                  disabled={!hasSubmissionContent || phase === 'pending'}
                  onClick={() => {
                    void submit();
                  }}
                  className={submitClasses}
                >
                  {phase === 'pending'
                    ? t('home.feedback.submitPending')
                    : t('home.feedback.submit')}
                </button>
              )}
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
